import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "sonner";

const API_URL = `${import.meta.env.VITE_API_URL}/orders`;

// Types
export interface OrderProduct {
  _id: string;
  productName: string;
  colorName: string;
  colorCode: string;
  bunch: number;
  image: string;
}

export interface OrderItem {
  _id: string;
  category: { name: string; _id: string };
  branch: { name: string; _id: string };
  s01: string;
  s02: string;
  s03: string;
  farm: string;
  country: string;
  price: number;
  quantity: number;
  subtotal: number;
  product: OrderProduct;
}

export interface OrderUser {
  _id: string;
  name: string;
  email: string;
  phone: string;
  role?: number;
}

export interface Order {
  _id: string;
  orderNumber: string;
  totalPrice: number;
  itemCount: number;
  items: OrderItem[];
  orderedBy: OrderUser;
  lastUpdatedBy?: OrderUser;
  orderNotes: string;
  adminNotes?: string;
  shippingCost?: number;
  deliveryDate: string;
  deliveryTime?: string;
  status: string;
  address: string;
  branch: string;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateOrderPayload extends Partial<Order> {
  _id: string;
}

export interface UpdateOrderItemsPayload {
  orderId: string;
  items: OrderItem[];
}

export interface UpdateOrderItemsResponse {
  message: string;
  order: Order;
}

// Query Keys
export const orderKeys = {
  all: ["orders"] as const,
  lists: () => [...orderKeys.all, "list"] as const,
  list: (filters: Record<string, unknown>) => [...orderKeys.lists(), filters] as const,
  details: () => [...orderKeys.all, "detail"] as const,
  detail: (id: string) => [...orderKeys.details(), id] as const,
};

// Fetch order details
const fetchOrderDetails = async (id: string): Promise<Order> => {
  const { data } = await axios.get(`${API_URL}/${id}`);
  return data;
};

// Update order
const updateOrder = async (order: UpdateOrderPayload): Promise<{ message: string }> => {
  const { data } = await axios.put(API_URL, { order });
  return data;
};

// Update order shipping cost
const updateOrderShippingCost = async ({
  orderId,
  shippingCost,
}: {
  orderId: string;
  shippingCost: number;
}): Promise<{ message: string }> => {
  const { data } = await axios.put(`${API_URL}/${orderId}/shipping`, { shippingCost });
  return data;
};

// Update order items (quantity changes and removals)
const updateOrderItems = async ({
  orderId,
  items,
}: UpdateOrderItemsPayload): Promise<UpdateOrderItemsResponse> => {
  const { data } = await axios.put(`${API_URL}/${orderId}/items`, { items });
  return data;
};

// Hooks
export const useOrderDetails = (id: string) => {
  return useQuery({
    queryKey: orderKeys.detail(id),
    queryFn: () => fetchOrderDetails(id),
    enabled: !!id,
  });
};

export const useUpdateOrder = (options?: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateOrder,
    onSuccess: (data) => {
      toast.success("Success!", {
        description: "Your order has been updated successfully",
      });
      // Invalidate and refetch order queries
      queryClient.invalidateQueries({ queryKey: orderKeys.all });
      options?.onSuccess?.();
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message ||
            error.message
          : "Failed to update order";
      toast.error("Something went wrong", {
        description: message,
      });
    },
  });
};

export const useUpdateOrderShippingCost = (options?: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateOrderShippingCost,
    onSuccess: (data) => {
      toast.success("Success!", {
        description: "Shipping cost updated successfully",
      });
      queryClient.invalidateQueries({ queryKey: orderKeys.all });
      options?.onSuccess?.();
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message ||
            error.message
          : "Failed to update shipping cost";
      toast.error("Something went wrong", {
        description: message,
      });
    },
  });
};

export const useUpdateOrderItems = (options?: { onSuccess?: (order: Order) => void }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateOrderItems,
    onSuccess: (data) => {
      toast.success("Success!", {
        description: "Your order items have been updated successfully",
      });
      // Update the cached order data directly
      queryClient.setQueryData(orderKeys.detail(data.order._id), data.order);
      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: orderKeys.lists() });
      options?.onSuccess?.(data.order);
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message ||
            error.message
          : "Failed to update order items";

      toast.error("Something went wrong", {
        description: message,
      });
    },
  });
};
